/**
 * /comparisons/:id — DIFF-2 comparison report.
 *
 * Renders a saved comparison's report as a dense, aligned two-column data
 * table (Estimate A | Estimate B), functionally like Xactimate's Differences
 * Report but cleaner: a compact summary band, a toggle between the "whole
 * estimate" order and a split "Agreed / Differences" view, and a per-row
 * status glyph (=, ~, Δ, −, +, ?, ≡). Deliberately NOT stacked cards — this
 * is a scan-first, spreadsheet-dense layout for 500+ line estimates.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, FileDiff, Loader2 } from 'lucide-react';

import { ComparisonsAPI } from '../lib/comparisonsApi';
import type {
  ComparisonDetail,
  DiffEntry,
  DiffKind,
  CanonicalItem,
  RoomSection,
  Theme,
  ThemeGroup,
  NarrativeProse,
} from '../lib/comparisonsApi';
import { cn, getErrorMessage } from '../lib/utils';
import ReconciliationWizard from './ReconciliationWizard';
import ReverseCompilePanel, { urlWantsScope, useReverseCompile } from './comparison/ReverseCompilePanel';

// 'whole' (unreadable flat list) removed per founder (B-661); By-room + split remain.
// 'scope' is T-0800's reverse compile, shown only when the server offers it.
type ViewMode = 'room' | 'split' | 'scope';
const VIEW_TABS: [ViewMode, string][] = [['room', 'By room'], ['split', 'Agreed & Differences'], ['scope', 'Scope']];

// Row-level status glyph + tone. Distinct from KIND_META's verbose label —
// this is the narrow status column at the right edge of each row.
const KIND_STATUS: Record<DiffKind, { glyph: string; cls: string; title: string }> = {
  SAME: { glyph: '=', cls: 'text-emerald-600 dark:text-emerald-400', title: 'Agreed' },
  QTY_DELTA: { glyph: 'Δ', cls: 'text-amber-600 dark:text-amber-400', title: 'Qty differs' },
  VARIANT: { glyph: '~', cls: 'text-amber-600 dark:text-amber-400', title: 'Variant' },
  SWAP_CANDIDATE: { glyph: '~', cls: 'text-amber-600 dark:text-amber-400', title: 'Possible swap' },
  CUSTOM_PRICE_DELTA: { glyph: '~', cls: 'text-amber-600 dark:text-amber-400', title: 'Price differs' },
  ADDED: { glyph: '+', cls: 'text-sky-600 dark:text-sky-400', title: 'Added in B' },
  REMOVED: { glyph: '−', cls: 'text-rose-600 dark:text-rose-400', title: 'Removed in B' },
  UNMAPPED: { glyph: '?', cls: 'text-muted-foreground', title: 'Unmapped' },
  EQUIVALENT: { glyph: '≡', cls: 'text-teal-600 dark:text-teal-400', title: 'Equivalent' },
};

function isAgreedRow(entry: DiffEntry): boolean {
  // Mirror the backend scope-agreement rule EXACTLY (reconcile.is_agreed):
  // an identical item is agreed, and a QTY_DELTA is agreed ONLY when it came
  // from measurement-group reconciliation (same item, measured differently).
  // A discrete qty change is a genuine (contested) difference — do not mute it.
  if (entry.kind === 'SAME') return true;
  if (entry.kind === 'QTY_DELTA' && entry.qty_delta_kind === 'MEASUREMENT_GROUP') return true;
  // Same surface + a small qty delta (±10%) is a sketch difference → agreed.
  // MUST match SAME_SURFACE_RATIO_TOL in app/services/diff/classify.py.
  if (
    entry.kind === 'QTY_DELTA' &&
    entry.same_surface &&
    entry.qty_ratio != null &&
    Math.abs(entry.qty_ratio - 1.0) <= 0.1
  )
    return true;
  return false;
}

function codeLabel(item: { cat: string; sel: string; activity: string | null } | null): string {
  if (!item) return '';
  return [item.cat, item.sel, item.activity].filter(Boolean).join(' ');
}

function polarityLabel(polarity: DiffEntry['polarity']): string | null {
  if (polarity === 'UPGRADE') return 'Upgrade';
  if (polarity === 'DOWNGRADE') return 'Downgrade';
  return null;
}

function polarityCls(polarity: DiffEntry['polarity']): string {
  if (polarity === 'UPGRADE') return 'text-emerald-700 dark:text-emerald-300';
  if (polarity === 'DOWNGRADE') return 'text-rose-700 dark:text-rose-300';
  return '';
}

const SummaryStat: React.FC<{ label: string; value: number; cls: string }> = ({
  label,
  value,
  cls,
}) => (
  <div className={cn('rounded-md border px-2.5 py-1.5 text-center', cls)}>
    <p className="text-lg font-bold leading-tight">{value}</p>
    <p className="text-[10px] uppercase tracking-wide">{label}</p>
  </div>
);

const money = (n: number | null): string =>
  n == null ? '' : `${n < 0 ? '−' : ''}$${Math.abs(Math.round(n)).toLocaleString()}`;

/** Extended (line) cost = qty × unit_price, None-safe. INDICATIVE materiality
 *  only — unit_price is display, never a scope claim for non-bid lines (B-682). */
const extCost = (item: CanonicalItem | null): number | null =>
  item && item.qty != null && item.unit_price != null ? item.qty * item.unit_price : null;

/** One side (Estimate A or Estimate B) of a diff row. Empty side renders an em dash. */
const SideCell: React.FC<{ item: CanonicalItem | null }> = ({ item }) => {
  if (!item) {
    return (
      <td className="px-3 py-1 align-top text-sm text-muted-foreground">
        <span aria-hidden="true">—</span>
        <span className="sr-only">not present</span>
      </td>
    );
  }
  // Extended cost (qty × unit_price) is the materiality figure; the bare
  // unit_price is a rate. Show both compactly (e.g. "120 SF · $1.62 · $194"),
  // but only when a unit_price exists (never a false $0) — B-682.
  const ext = extCost(item);
  const meta = [
    item.room,
    item.qty != null ? `${item.qty} ${item.unit || ''}`.trim() : null,
    item.unit_price != null ? `$${item.unit_price.toFixed(2)}` : null,
    ext != null ? money(ext) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  // Prefer the resolved description ("...to the floor" over "...to {V}").
  const rawDesc = item.display_description || item.description || '';
  // Some sides (typically the PDF extraction) bake "R&R" into the description
  // text while the ESX side carries it as the "&" activity glyph. Strip that
  // leading token into the badge so BOTH columns read the same action and the
  // size/grade differentiator isn't pushed out of view (B-683).
  const rrMatch = rawDesc.match(/^\s*(R\s*&\s*R|R\s*and\s*R)\b[\s:.,-]*/i);
  const desc = rrMatch ? rawDesc.slice(rrMatch[0].length) : rawDesc;
  // A description-embedded "R&R" is an explicit action statement — prefer it
  // over a glyph that a parser may have flattened to "+".
  const effectiveAction = rrMatch ? 'Remove & Replace' : item.action_label;
  // Show the action wording when it's a meaningful, non-default action — a bare
  // "+" (Replace) is the implied default and would just be noise (B-635). "&"
  // (R&R) always shows, and a description-embedded R&R promotes to the badge so
  // the two columns read alike.
  const showAction =
    Boolean(effectiveAction) &&
    (Boolean(rrMatch) || (item.activity !== '+' && item.activity !== '' && item.activity !== null));

  return (
    <td className="px-3 py-1 align-top">
      <div className="flex items-baseline gap-2">
        <span className="shrink-0 font-mono text-[11px] text-muted-foreground">{codeLabel(item)}</span>
        {showAction && (
          <span className="shrink-0 rounded bg-muted px-1 text-[10px] font-medium text-muted-foreground">
            {effectiveAction}
          </span>
        )}
        {/* Wrap to 2 lines (not hard-truncate) so a size/grade differentiator at
            the tail stays visible; keep the full text in the tooltip (B-683). */}
        <span className="line-clamp-2 min-w-0 text-sm text-foreground" title={rawDesc}>
          {desc}
        </span>
      </div>
      {meta && <div className="truncate text-[11px] text-muted-foreground">{meta}</div>}
    </td>
  );
};

const THEME_TONE: Record<string, string> = {
  WATER_REMEDIATION: 'border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/15',
  HEIGHT_COVERAGE: 'border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15',
  QUANTITY: 'border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15',
  GRADE_QUALITY: 'border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15',
  SUBSTITUTION: 'border bg-muted',
  OMITTED_SCOPE: 'border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/15',
  ADDED_SCOPE: 'border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/15',
  PRICE_LIST: 'border-indigo-200 dark:border-indigo-500/30 bg-indigo-50 dark:bg-indigo-500/15',
  SKETCH: 'border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/15',
};

/** The generated neutral prose narrative (on demand). */
const NarrativeProseView: React.FC<{ prose: NarrativeProse }> = ({ prose }) => (
  <div className="space-y-3 border-t bg-muted/60 px-3 py-3">
    {prose.overview && <p className="text-[13px] leading-relaxed text-foreground">{prose.overview}</p>}
    {prose.sections.map((s, i) => (
      <div key={i}>
        <p className="text-[12px] font-semibold text-foreground">{s.heading}</p>
        <p className="text-[13px] leading-relaxed text-foreground">{s.narrative}</p>
        {s.basis && (
          <p className="mt-0.5 text-[11px] italic text-muted-foreground">Basis: {s.basis}</p>
        )}
      </div>
    ))}
    {prose.disclaimer && (
      <p className="border-t pt-2 text-[10px] leading-snug text-muted-foreground">{prose.disclaimer}</p>
    )}
  </div>
);

// Scope-only theme kinds — their code-groups read better clustered by
// Xactimate category so related omissions (all HMR/asbestos-family items) sit
// together instead of scattered (B-684).
const SCOPE_ONLY_KINDS = new Set(['OMITTED_SCOPE', 'ADDED_SCOPE']);

interface CategoryCluster {
  category: string;
  subtotal: number | null;
  groups: ThemeGroup[];
}

/** Cluster a theme's code-groups by Xactimate category (the code's first token,
 *  e.g. "HMR"), summing each cluster's $ and ordering by dollar impact (B-684). */
function clusterByCategory(groups: ThemeGroup[]): CategoryCluster[] {
  const buckets = new Map<string, ThemeGroup[]>();
  const order: string[] = [];
  for (const g of groups) {
    const code = (g.a_code || g.b_code || '').trim();
    const cat = code.split(/\s+/)[0] || '—';
    if (!buckets.has(cat)) {
      buckets.set(cat, []);
      order.push(cat);
    }
    buckets.get(cat)!.push(g);
  }
  const clusters = order.map((category) => {
    const gs = buckets.get(category)!;
    const amts = gs.map((g) => g.amount).filter((a): a is number => a != null);
    const subtotal = amts.length ? amts.reduce((s, a) => s + a, 0) : null;
    return { category, subtotal, groups: gs };
  });
  // Order by descending dollar impact (unpriced clusters sink to the bottom).
  clusters.sort((a, b) => Math.abs(b.subtotal ?? 0) - Math.abs(a.subtotal ?? 0));
  return clusters;
}

/** Scope-only theme body: code-groups grouped under a per-category sub-header
 *  with a dollar subtotal, so related omissions/additions read together (B-684). */
const ScopeCategoryClusters: React.FC<{ groups: ThemeGroup[] }> = ({ groups }) => {
  const clusters = clusterByCategory(groups);
  // A single category adds no clustering value — render flat.
  if (clusters.length <= 1) {
    return (
      <div className="mt-2 space-y-1 border-l-2 border pl-2">
        {groups.map((g, j) => (
          <ThemeGroupRow key={j} group={g} />
        ))}
      </div>
    );
  }
  return (
    <div className="mt-2 space-y-2 border-l-2 border pl-2">
      {clusters.map((c) => (
        <div key={c.category}>
          <div className="flex items-center justify-between border-b border pb-0.5">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {c.category}
              <span className="ml-1 font-sans normal-case text-muted-foreground">
                {c.groups.length} code{c.groups.length === 1 ? '' : 's'}
              </span>
            </span>
            {c.subtotal != null && (
              <span className="text-[11px] font-semibold text-muted-foreground">{money(c.subtotal)}</span>
            )}
          </div>
          <div className="mt-1 space-y-1">
            {c.groups.map((g, j) => (
              <ThemeGroupRow key={j} group={g} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/** Methodology roll-up: the differences distilled into discussable themes. */
const MethodologySummary: React.FC<{ themes: Theme[]; comparisonId?: string }> = ({
  themes,
  comparisonId,
}) => {
  const [open, setOpen] = useState(true);
  const [prose, setProse] = useState<NarrativeProse | null>(null);
  const [generating, setGenerating] = useState(false);
  const [narrErr, setNarrErr] = useState<string | null>(null);

  const generate = useCallback(async () => {
    if (!comparisonId) return;
    setGenerating(true);
    setNarrErr(null);
    try {
      setProse(await ComparisonsAPI.generateNarrative(comparisonId));
    } catch (err) {
      setNarrErr(getErrorMessage(err));
    } finally {
      setGenerating(false);
    }
  }, [comparisonId]);

  if (!themes || themes.length === 0) return null;
  return (
    <section className="rounded-lg border bg-card shadow-sm">
      <div className="flex w-full items-center justify-between border-b bg-muted px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-left"
        >
          <ChevronRight className={cn('h-4 w-4 transition-transform', open && 'rotate-90')} />
          <h3 className="text-sm font-semibold text-foreground">
            Methodology summary — {themes.length} theme{themes.length === 1 ? '' : 's'}
          </h3>
        </button>
        {comparisonId && (
          <button
            type="button"
            onClick={generate}
            disabled={generating}
            className="rounded-md border px-2.5 py-1 text-[12px] font-medium text-foreground hover:bg-card disabled:opacity-60"
          >
            {generating ? 'Generating…' : prose ? 'Regenerate narrative' : 'Generate narrative'}
          </button>
        )}
      </div>
      {narrErr && <p className="px-3 py-1.5 text-[11px] text-red-700 dark:text-red-300">{narrErr}</p>}
      {prose && <NarrativeProseView prose={prose} />}
      {open && (
        <div className="divide-y">
          {themes.map((t, i) => (
            <details key={`${t.kind}-${i}`} className="group px-3 py-2">
              <summary className="flex cursor-pointer items-start justify-between gap-3 list-none">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                        THEME_TONE[t.kind] || 'border bg-muted',
                      )}
                    >
                      {t.direction || t.kind.replace(/_/g, ' ').toLowerCase()}
                    </span>
                    <span className="truncate text-sm font-semibold text-foreground">{t.title}</span>
                  </div>
                  <p className="mt-0.5 text-[12px] text-muted-foreground">{t.summary}</p>
                </div>
                <div className="shrink-0 text-right">
                  {t.amount != null && (
                    <span className="text-sm font-semibold text-foreground">{money(t.amount)}</span>
                  )}
                  <ChevronRight className="ml-auto mt-1 h-3.5 w-3.5 text-muted-foreground transition-transform group-open:rotate-90" />
                </div>
              </summary>
              {t.groups && t.groups.length > 0 ? (
                SCOPE_ONLY_KINDS.has(t.kind) ? (
                  <ScopeCategoryClusters groups={t.groups} />
                ) : (
                  <div className="mt-2 space-y-1 border-l-2 border pl-2">
                    {t.groups.map((g, j) => (
                      <ThemeGroupRow key={j} group={g} />
                    ))}
                  </div>
                )
              ) : (
                t.evidence.length > 0 && (
                  <ul className="mt-2 space-y-0.5 border-l-2 border pl-3 text-[11px] text-muted-foreground">
                    {t.evidence.map((e, j) => (
                      <li key={j} className="flex flex-wrap items-baseline gap-x-2">
                        {e.room && <span className="text-muted-foreground">{e.room}</span>}
                        {e.code && <span className="font-mono text-muted-foreground">{e.code}</span>}
                        <span className="text-foreground">{e.description}</span>
                        {e.detail && <span className="text-muted-foreground">· {e.detail}</span>}
                      </li>
                    ))}
                  </ul>
                )
              )}
            </details>
          ))}
        </div>
      )}
    </section>
  );
};

const AXIS_TONE: Record<string, string> = {
  grade: 'bg-purple-50 dark:bg-purple-500/15 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30',
  size: 'bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30',
  substitution: 'bg-orange-50 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-500/30',
};

/**
 * One code-group inside a theme (B-657): the aggregate row (code, axis, $, qty
 * totals, room/line counts, dual descriptions for swaps) expandable to the
 * per-room lines that make it up.
 */
const ThemeGroupRow: React.FC<{ group: ThemeGroup }> = ({ group: g }) => {
  const isSwap = !!(g.a_code && g.b_code && g.a_code !== g.b_code);
  const codeLabel = isSwap ? `${g.a_code} → ${g.b_code}` : g.a_code || g.b_code;
  const qtyLabel =
    g.a_qty_total != null || g.b_qty_total != null
      ? `${g.a_qty_total ?? '—'} → ${g.b_qty_total ?? '—'}${g.unit ? ` ${g.unit}` : ''}`
      : '';
  return (
    <details className="group/g rounded border border bg-muted/60 px-2 py-1">
      <summary className="flex cursor-pointer list-none items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono text-[11px] font-semibold text-foreground">{codeLabel}</span>
            {g.axis && (
              <span className={cn('rounded px-1 py-px text-[9px] font-semibold uppercase', AXIS_TONE[g.axis] || '')}>
                {g.axis}
              </span>
            )}
            {isSwap && g.descriptions_match === false && (
              <span className="rounded bg-rose-50 dark:bg-rose-500/15 px-1 py-px text-[9px] font-semibold uppercase text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-500/30">
                descriptions differ
              </span>
            )}
            <span className="text-[10px] text-muted-foreground">
              {g.room_count > 0 ? `${g.room_count} room${g.room_count === 1 ? '' : 's'}` : `${g.item_count} line${g.item_count === 1 ? '' : 's'}`}
            </span>
          </div>
          {/* Dual descriptions so a reviewer can judge a substitution (B-658). */}
          {isSwap ? (
            <div className="mt-0.5 space-y-px text-[11px]">
              <div className="text-muted-foreground"><span className="text-muted-foreground">A:</span> {g.a_description}</div>
              <div className="text-muted-foreground"><span className="text-muted-foreground">B:</span> {g.b_description}</div>
            </div>
          ) : (
            <p className="mt-0.5 text-[11px] text-muted-foreground">{g.a_description || g.b_description}</p>
          )}
          {/* Systematic quantity-pattern insight (B-660). */}
          {g.pattern && (
            <p className="mt-1 rounded bg-indigo-50 dark:bg-indigo-500/15 px-1.5 py-1 text-[10.5px] text-indigo-800 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-500/20">
              <span aria-hidden="true">◆ </span>{g.pattern}
            </p>
          )}
        </div>
        <div className="shrink-0 text-right">
          {g.amount != null && (
            <div className="text-[12px] font-semibold text-foreground">{money(g.amount)}</div>
          )}
          {qtyLabel && <div className="text-[10px] text-muted-foreground">{qtyLabel}</div>}
          <ChevronRight className="ml-auto mt-0.5 h-3 w-3 text-muted-foreground transition-transform group-open/g:rotate-90" />
        </div>
      </summary>
      {/* Per-room drill-down. */}
      <ul className="mt-1 space-y-0.5 border-t border pt-1 text-[10.5px] text-muted-foreground">
        {g.evidence.map((e, k) => (
          <li key={k} className="flex flex-wrap items-baseline gap-x-2">
            {e.room && <span className="text-muted-foreground">{e.room}</span>}
            <span className="text-muted-foreground">{e.detail}</span>
            {e.amount != null && <span className="text-muted-foreground">· {money(e.amount)}</span>}
          </li>
        ))}
      </ul>
    </details>
  );
};

/** Per-room block: a room header (agreed/different tallies) + its A|B table. */
const RoomSectionsView: React.FC<{ sections: RoomSection[] }> = ({ sections }) => {
  if (!sections || sections.length === 0) {
    return <p className="p-6 text-center text-sm text-muted-foreground">No rooms.</p>;
  }
  return (
    <div className="space-y-4">
      <p className="rounded-md border border bg-muted px-3 py-1.5 text-[11px] text-muted-foreground">
        Grouped by <span className="font-medium text-foreground">Estimate A</span>&apos;s rooms. When
        a line was filed under a different room in Estimate B, the trailing label reads{' '}
        <span className="text-amber-700 dark:text-amber-300">A&nbsp;→&nbsp;B</span> (e.g. a building-wide{' '}
        <span className="font-medium">Footprint</span> room vs a per-room placement) — a bookkeeping
        difference, not a scope difference.
      </p>
      {sections.map((s) => (
        <section
          key={s.key}
          className="overflow-hidden rounded-lg border bg-card shadow-sm [content-visibility:auto] [contain-intrinsic-size:0_240px]"
        >
          <div className="flex items-center justify-between border-b bg-muted px-3 py-1.5">
            <h3 className="text-sm font-semibold text-foreground">{s.room}</h3>
            <div className="flex items-center gap-2 text-[11px] font-medium">
              {(() => {
                // Indicative extended-cost total for the section, with the net
                // A→B swing when both sides are priced. Display materiality only,
                // never a scope claim for non-bid lines (B-682).
                if (s.a_total == null && s.b_total == null) return null;
                const net =
                  s.a_total != null && s.b_total != null ? s.b_total - s.a_total : null;
                return (
                  <span
                    className="text-muted-foreground"
                    title="Indicative extended-cost total (qty × unit price) — display materiality, not a scope claim"
                  >
                    {money(s.a_total ?? s.b_total)}
                    {net != null && Math.round(net) !== 0 && (
                      <span className={net > 0 ? 'ml-1 text-sky-700 dark:text-sky-300' : 'ml-1 text-rose-700 dark:text-rose-300'}>
                        ({net > 0 ? '+' : ''}
                        {money(net)})
                      </span>
                    )}
                  </span>
                );
              })()}
              <span className="text-emerald-700 dark:text-emerald-300">{s.agreed} agreed</span>
              {s.different > 0 && <span className="text-amber-700 dark:text-amber-300">{s.different} different</span>}
            </div>
          </div>
          <ComparisonTable entries={s.entries} emptyLabel="No line items." mutedFn={isAgreedRow} />
        </section>
      ))}
    </div>
  );
};

interface DiffTableRowProps {
  entry: DiffEntry;
  muted?: boolean;
}

/** A single tight, aligned two-column row: Estimate A | Estimate B | status. */
const DiffTableRow: React.FC<DiffTableRowProps> = ({ entry, muted }) => {
  const status = KIND_STATUS[entry.kind];
  const pLabel = polarityLabel(entry.polarity);
  const isMeasurementAgreed =
    entry.kind === 'QTY_DELTA' && entry.qty_delta_kind === 'MEASUREMENT_GROUP' && isAgreedRow(entry);

  // Room labels: grouping is by Estimate A's room, so when the two estimates
  // filed a line under different rooms (e.g. Estimate A's building-wide
  // "Footprint" vs Estimate B's per-room "Living Room unit 1") show both sides
  // as "A → B" so the trailing label is self-explanatory (B-662).
  const aRoom = entry.baseline?.room?.trim();
  const bRoom = entry.comparison?.room?.trim();
  const roomsDiffer = Boolean(entry.room_moved && aRoom && bRoom);
  const roomLabel = roomsDiffer ? `${aRoom} → ${bRoom}` : aRoom || bRoom || entry.room || '';
  const roomTitle = roomsDiffer
    ? `Same line item, filed under different rooms — Estimate A: "${aRoom}", Estimate B: "${bRoom}". A bookkeeping difference, not a scope difference.`
    : undefined;

  const notesParts: string[] = [];
  if (pLabel) notesParts.push(pLabel);
  if (isMeasurementAgreed) {
    notesParts.push(
      `Measured differently${entry.qty_ratio != null ? ` (×${entry.qty_ratio.toFixed(2)})` : ''}`,
    );
  } else if (entry.qty_delta != null && entry.qty_delta !== 0) {
    notesParts.push(
      `Qty ${entry.qty_delta > 0 ? '+' : ''}${entry.qty_delta}${
        entry.qty_ratio != null ? ` (×${entry.qty_ratio.toFixed(2)})` : ''
      }`,
    );
  }
  if (entry.unit_price_delta != null && entry.unit_price_delta !== 0) {
    notesParts.push(`Price ${entry.unit_price_delta > 0 ? '+' : ''}$${entry.unit_price_delta.toFixed(2)}`);
  }
  if (entry.notes.length > 0) notesParts.push(...entry.notes);

  // Indicative dollar swing for a contested row: extended B − extended A, or
  // qty_delta × the baseline unit rate when only one side is priced. Display
  // materiality only — NOT a base-rate scope claim (B-682).
  const extA = extCost(entry.baseline);
  const extB = extCost(entry.comparison);
  let dollarDelta: number | null = null;
  if (extA != null && extB != null) dollarDelta = extB - extA;
  else if (entry.qty_delta != null && entry.baseline?.unit_price != null)
    dollarDelta = entry.qty_delta * entry.baseline.unit_price;
  const dollarChip =
    !muted && dollarDelta != null && Math.round(dollarDelta) !== 0
      ? `${dollarDelta > 0 ? '+' : ''}${money(dollarDelta)}`
      : null;

  // Compact size/grade indicator for a corroborated variant/swap (B-683): the
  // backend classifier already resolved which axis the two codes differ on.
  const axisBadge =
    entry.variant_axis === 'grade' || entry.variant_axis === 'size'
      ? entry.variant_axis
      : null;

  const wb = entry.whole_building;

  return (
    <>
    <tr
      className={cn(
        'border-b border last:border-b-0',
        muted ? 'bg-card text-muted-foreground' : 'bg-card',
        '[content-visibility:auto] [contain-intrinsic-size:0_40px]',
      )}
    >
      <SideCell item={entry.baseline} />
      <SideCell item={entry.comparison} />
      <td className="w-24 px-2 py-1 align-top text-right">
        <div className="flex items-center justify-end gap-1">
          {axisBadge && (
            <span
              title={`This pair differs on ${axisBadge} — same work, ${axisBadge === 'grade' ? 'a different quality tier' : 'a different size'}. Not a different item (B-683).`}
              className={cn(
                'shrink-0 rounded px-1 text-[9px] font-semibold uppercase',
                AXIS_TONE[axisBadge] || '',
              )}
            >
              {axisBadge}
            </span>
          )}
          {roomLabel && (
            <span
              title={roomTitle}
              className={cn(
                'hidden truncate text-[11px] lg:inline',
                roomsDiffer ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground',
              )}
            >
              {roomLabel}
            </span>
          )}
          <span
            className={cn('text-sm font-semibold', status.cls, muted && 'opacity-70')}
            title={status.title}
          >
            {status.glyph}
          </span>
        </div>
      </td>
      {(notesParts.length > 0 || dollarChip) && (
        <td className="hidden max-w-[16rem] py-1 pl-2 pr-3 text-[11px] text-muted-foreground xl:table-cell">
          <div className="flex items-center gap-1.5">
            {dollarChip && (
              <span
                title="Indicative dollar swing (extended A vs B) — display materiality, not a base-rate scope claim"
                className={cn(
                  'shrink-0 rounded px-1 font-medium tabular-nums',
                  dollarDelta! > 0
                    ? 'bg-sky-50 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300'
                    : 'bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300',
                )}
              >
                {dollarChip}
              </span>
            )}
            {notesParts.length > 0 && <span className="truncate">{notesParts.join(' · ')}</span>}
          </div>
        </td>
      )}
      {notesParts.length === 0 && !dollarChip && <td className="hidden xl:table-cell" />}
    </tr>
    {wb && (
      <tr className="border-b border bg-amber-50/40 dark:bg-amber-500/10">
        <td colSpan={4} className="px-3 py-1.5">
          <details className="group text-[11px]">
            <summary className="flex cursor-pointer list-none items-center gap-1.5 text-amber-800 dark:text-amber-300">
              <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
              <span className="font-medium">Building-wide vs distributed</span>
              <span className="text-muted-foreground">
                — {wb.whole_room} {wb.whole_qty ?? '?'} {wb.unit} (Estimate {wb.whole_side}) vs{' '}
                {wb.distributed_rooms.length} rooms totalling {wb.distributed_total ?? '?'} {wb.unit}
              </span>
            </summary>
            <ul className="mt-1 grid grid-cols-2 gap-x-6 gap-y-0.5 pl-4 text-muted-foreground sm:grid-cols-3">
              {wb.distributed_rooms.map((r, i) => (
                <li key={i} className="flex justify-between gap-2">
                  <span className="truncate">{r.room}</span>
                  <span className="shrink-0 font-mono text-muted-foreground">
                    {r.qty ?? '?'} {r.unit}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        </td>
      </tr>
    )}
    </>
  );
};

/** Sticky "Estimate A | Estimate B | (status)" header row shared by every table. */
const ComparisonTableHead: React.FC = () => (
  <thead className="sticky top-0 z-10 bg-muted text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
    <tr>
      <th className="px-3 py-1.5 font-semibold">Estimate A</th>
      <th className="px-3 py-1.5 font-semibold">Estimate B</th>
      <th className="w-24 px-2 py-1.5 text-right font-semibold"></th>
      <th className="hidden xl:table-cell"></th>
    </tr>
  </thead>
);

interface ComparisonTableProps {
  entries: DiffEntry[];
  emptyLabel: string;
  mutedFn?: (entry: DiffEntry) => boolean;
}

/** Dense spreadsheet-style table for a list of diff entries. */
const ComparisonTable: React.FC<ComparisonTableProps> = ({ entries, emptyLabel, mutedFn }) => {
  if (entries.length === 0) {
    return <p className="p-6 text-center text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-fixed border-collapse text-sm">
        <colgroup>
          <col className="w-[42%]" />
          <col className="w-[42%]" />
          <col className="w-24" />
          <col className="hidden xl:table-column" />
        </colgroup>
        <ComparisonTableHead />
        <tbody>
          {entries.map((entry, i) => (
            <DiffTableRow key={entry.key || i} entry={entry} muted={mutedFn?.(entry)} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ComparisonReportPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [detail, setDetail] = useState<ComparisonDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>(urlWantsScope() ? 'scope' : 'room');
  const [warningsOpen, setWarningsOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const rc = useReverseCompile(id);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await ComparisonsAPI.get(id);
      setDetail(res);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-center justify-center gap-2 p-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading comparison…
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-4 text-sm text-red-800 dark:text-red-300">
          {error || 'Comparison not found.'}
        </div>
      </div>
    );
  }

  const { report } = detail;
  const { summary } = report;

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm font-medium text-muted-foreground">
        <Link to="/" className="hover:text-primary">Dashboard</Link>
        <ChevronRight className="mx-2 h-4 w-4" />
        <Link to="/comparisons" className="hover:text-primary">Comparisons</Link>
        <ChevronRight className="mx-2 h-4 w-4" />
        <span className="text-foreground">{detail.name || 'Comparison'}</span>
      </nav>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold tracking-tight">
            <FileDiff className="h-5 w-5 text-primary" />
            {detail.name || 'Comparison'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {report.baseline_filename} <span className="mx-1">vs.</span> {report.comparison_filename}
          </p>
          {(report.baseline_price_list || report.comparison_price_list) && (
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              Price list:{' '}
              <span className="font-mono">{report.baseline_price_list || '—'}</span>
              <span className="mx-1">vs.</span>
              <span className="font-mono">{report.comparison_price_list || '—'}</span>
              {report.baseline_price_list &&
                report.comparison_price_list &&
                report.baseline_price_list !== report.comparison_price_list && (
                  <span className="ml-1 rounded bg-amber-100 dark:bg-amber-500/20 px-1 text-amber-800 dark:text-amber-300">
                    differ — small base-rate gaps are price-list version, not scope
                  </span>
                )}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setWizardOpen(true)}
          className="rounded-md border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
        >
          Reconcile
        </button>
      </div>

      {wizardOpen && (
        <ReconciliationWizard
          comparison={detail}
          onClose={(updated) => {
            setWizardOpen(false);
            if (updated) setDetail(updated);
          }}
        />
      )}

      {/* Summary band */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <SummaryStat label="Agreed" value={summary.agreed} cls="border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300" />
        <SummaryStat label="Contested" value={summary.contested} cls="border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300" />
        <SummaryStat label="Added" value={summary.added} cls="border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/15 text-sky-800 dark:text-sky-300" />
        <SummaryStat label="Removed" value={summary.removed} cls="border-rose-200 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/15 text-rose-800 dark:text-rose-300" />
        <SummaryStat label="Unmapped" value={summary.unmapped} cls="border bg-muted text-muted-foreground" />
      </div>

      {report.warnings.length > 0 && (
        <div className="rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15">
          <button
            type="button"
            onClick={() => setWarningsOpen((v) => !v)}
            className="flex w-full items-center justify-between px-3 py-1.5 text-left text-xs font-medium text-amber-800 dark:text-amber-300"
          >
            {report.warnings.length} warning{report.warnings.length === 1 ? '' : 's'}
            <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', warningsOpen && 'rotate-90')} />
          </button>
          {warningsOpen && (
            <ul className="space-y-0.5 border-t border-amber-200 dark:border-amber-500/30 px-3 py-1.5 text-[11px] text-amber-800 dark:text-amber-300">
              {report.warnings.map((w, i) => (
                <li key={i}>· {w}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Methodology roll-up */}
      <MethodologySummary themes={report.themes} comparisonId={detail.id} />

      {/* View toggle */}
      <div className="inline-flex rounded-md border bg-card p-1 text-sm">
        {VIEW_TABS.map(([mode, label]) => (mode === 'scope' && rc.status === 'unavailable' ? null : (
          <button
            key={mode}
            type="button"
            onClick={() => setView(mode)}
            className={cn(
              'rounded px-3 py-1.5 font-medium transition-colors',
              view === mode ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted',
            )}
          >
            {label}
          </button>
        )))}
      </div>

      {view === 'scope' ? (
        <ReverseCompilePanel state={rc} />
      ) : view === 'room' ? (
        <RoomSectionsView sections={report.room_sections} />
      ) : (
        <div className="space-y-4">
          <section className="rounded-lg border bg-card shadow-sm">
            <div className="border-b bg-emerald-50 dark:bg-emerald-500/15 px-3 py-1.5">
              <h3 className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                Agreed ({report.agreed.length})
              </h3>
            </div>
            <ComparisonTable entries={report.agreed} emptyLabel="Nothing agreed yet." />
          </section>

          <section className="rounded-lg border bg-card shadow-sm">
            <div className="border-b bg-amber-50 dark:bg-amber-500/15 px-3 py-1.5">
              <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                Differences ({report.different.length})
              </h3>
            </div>
            <ComparisonTable entries={report.different} emptyLabel="No differences found." />
          </section>
        </div>
      )}
    </div>
  );
};

export default ComparisonReportPage;
